import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Reveal } from "../components/site/Reveal";

const METHODE_IMAGE = "/images/hero.jpg";

const STEPS = [
  {
    number: "01",
    title: "La réflexion",
    subtitle: "Lire le lieu",
    intro:
      "Tout commence par une lecture stratégique du lieu. Avant toute décision, nous analysons votre terrain, votre bâti ou votre site dans ses dimensions visibles et invisibles.",
    blocks: [
      {
        label: "Le visible",
        text:
          "Notre conception bioclimatique pour l'optimisation de votre habitat et entreprise : l'orientation du lieu, la topographie, les contraintes climatiques."
      },
      {
        label: "L'invisible",
        text:
          "La géobiologie appliquée, qui s'appuie sur le ressenti, les baguettes de sourcier, à la manière des anciens bâtisseurs qui ne laissaient rien au hasard. Nous cherchons les réseaux telluriques, veines d'eau, organisation des flux, perturbations électromagnétiques qui peuvent altérer votre lieu et votre bien-être. Parfois quantifiable et mesurable (voir les problèmes rencontrés dans le domaine agricole avec les animaux et les perturbations électriques)."
      },
      {
        label: "Le Feng Shui structurel",
        text:
          "Un art millénaire chinois, à ne pas confondre avec le « New Age » qui n'est que décoration. Il a fait ses preuves maintes fois. De grandes entreprises s'en inspirent pour le placement de leurs lieux, leur marque, leur logo, au service de l'harmonie et du bien-être au travail."
      }
    ],
    closing:
      "Cette lecture conditionne toutes les décisions qui suivent. Un terrain mal compris génère des contraintes durables. Un lieu bien lu révèle des potentiels énergétiques et structurels qu'aucun plan standard n'aurait identifiés."
  },
  {
    number: "02",
    title: "La conception",
    subtitle: "Définir la stratégie",
    intro:
      "Une fois le lieu compris, nous élaborons la stratégie globale de votre projet : implantation, orientation, organisation des espaces, structuration des flux intérieurs et extérieurs, choix des matériaux naturels et biosourcés, cohérence bioclimatique et énergétique.",
    blocks: [
      {
        label: "Une cohérence d'ensemble",
        text:
          "Chaque décision est pensée en fonction de votre lieu, de vos usages et du bien-être de ceux qui l'habitent ou l'exploitent. L'objectif est toujours le même : un espace harmonisé, performant et durable."
      },
      {
        label: "Matériaux biosourcés",
        text:
          "Nos matériaux biosourcés sont là pour la performance énergétique et thermique du bâti, et pour votre santé."
      },
      {
        label: "Conception globale et assistance à maîtrise d'ouvrage",
        text:
          "Rozier Concept intervient en conception globale et en assistance à maîtrise d'ouvrage, en collaboration avec les professionnels habilités si besoin : architectes, maîtres d'œuvre, bureaux d'études. Notre rôle est de porter la cohérence d'ensemble et de garantir que chaque dimension de votre projet parle le même langage."
      }
    ],
    closing:
      "C'est notre expérience terrain qui nous guide pour créer des lieux uniques et vibrants."
  },
  {
    number: "03",
    title: "La réalisation",
    subtitle: "Construire avec rigueur",
    intro:
      "La stratégie prend forme. Nous réalisons les travaux relevant de nos domaines d'intervention et pouvons vous recommander des professionnels d'autres corps de métiers avec lesquels nous travaillons régulièrement.",
    blocks: [
      {
        label: "Nos métiers",
        list: [
          "Charpente, couverture, zinguerie",
          "Construction ossature bois, extensions, bardage, carport, pergola bois ou bioclimatique",
          "Isolation thermique par l'extérieur tout support",
          "Menuiseries tout support et aménagements intérieurs",
          "Aménagements extérieurs et paysagers : dallage, pavage, cours, clôtures, terrasses, plages de piscine"
        ]
      }
    ],
    closing:
      "Chacune de nos réalisations est l'aboutissement de notre réflexion. Elle traduit dans le visible ce que la lecture du lieu a révélé dans l'invisible."
  }
];

const HeroHeader = () => (
  <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
    <img
      src={METHODE_IMAGE}
      alt="La Méthode Rozier"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
    <div className="absolute inset-0 flex items-end">
      <div className="container-rozier pb-16 md:pb-20 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors text-xs uppercase tracking-[0.28em] mb-6"
          data-testid="methode-back-home"
        >
          <ArrowLeft size={14} />
          Accueil
        </Link>
        <p className="overline text-white/70 mb-4 sr-only">Notre approche</p>
        <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight font-light">
          La <span className="italic gold-text-shimmer">Méthode Rozier</span>
        </h1>
        <p className="mt-6 font-serif italic text-white/85 text-2xl md:text-3xl max-w-3xl leading-snug">
          Lire le lieu et comprendre avant d'agir, organiser avant de construire ou
          transformer.
        </p>
      </div>
    </div>
  </section>
);

const Devise = () => (
  <section className="py-24 md:py-32 bg-ivory" data-testid="methode-devise">
    <div className="container-rozier">
      <Reveal className="max-w-4xl mx-auto text-center">
        <p className="overline mb-8">Notre devise</p>
        <p className="font-serif italic text-charcoal text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight font-light">
          « L'invisible <span className="gold-text">structure</span> le visible. »
        </p>
        <div className="hairline w-40 mx-auto mt-12" />
        <p className="mt-10 text-graphite text-base md:text-lg leading-[1.9] max-w-2xl mx-auto">
          La Méthode Rozier est née d'une conviction profonde, nourrie depuis des
          années : on ne transforme pas un lieu sans l'avoir d'abord compris. Ce qui
          nous distingue, c'est le refus de traiter le bâti, le paysage et
          l'environnement comme des sujets séparés.
        </p>
      </Reveal>
    </div>
  </section>
);

const Philosophie = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="methode-philosophie">
    <div className="container-rozier grid grid-cols-12 gap-10">
      <Reveal className="col-span-12 md:col-span-5">
        <p className="overline mb-6">Notre philosophie</p>
        <h2 className="font-serif text-3xl md:text-5xl text-charcoal leading-[1.05] tracking-tight font-light">
          L'harmonie de la personne est en lien direct avec
          <span className="italic gold-text"> l'énergie du lieu</span>.
        </h2>
      </Reveal>
      <Reveal className="col-span-12 md:col-span-6 md:col-start-7" delay={150}>
        <p className="text-graphite text-base md:text-lg leading-[1.9]">
          La maison, l'appartement, le terrain, le lieu de vie ou de travail : tous
          dialoguent avec celui qui les habite. Nous sommes là pour créer cette
          harmonie.
        </p>
        <p className="mt-6 text-graphite text-base md:text-lg leading-[1.9]">
          La Méthode Rozier s'appuie sur des connaissances intenses, des formations
          et certifications issues de différents corps de métiers, dont l'expertise
          de géobiologie appliquée et celle du Feng Shui structurel, étudié en
          profondeur.
        </p>
        <p className="mt-6 text-graphite text-base md:text-lg leading-[1.9]">
          Contrairement aux idées reçues, nous ne travaillons pas dans la
          dispersion et ne lions pas notre travail à l'ésotérisme. Nous harmonisons
          vos lieux, habitats et entreprises de façon durable et au plus naturel
          possible, afin qu'ils soient sources de bien-être et de prospérité.
        </p>
      </Reveal>
    </div>
  </section>
);

const TroisTemps = () => (
  <section className="py-24 md:py-32 bg-charcoal text-white" data-testid="methode-trois-temps">
    <div className="container-rozier">
      <Reveal>
        <p className="overline text-white/60 mb-6">Une logique en trois temps</p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light max-w-4xl">
          Lire, organiser,
          <span className="italic gold-text"> construire ou transformer</span>.
        </h2>
      </Reveal>
      <Reveal delay={150}>
        <p className="mt-10 text-white/75 text-base md:text-lg leading-[1.9] max-w-3xl">
          Une logique qui s'applique à chaque projet que nous accompagnons : en
          construction neuve comme en rénovation, sur un terrain vierge comme sur un
          bâti existant depuis des années. Pour les particuliers comme pour les
          professionnels.
        </p>
      </Reveal>
    </div>
  </section>
);

const StepBlock = ({ step, index }) => {
  const reversed = index % 2 === 1;
  return (
    <section
      className={`py-24 md:py-28 ${index % 2 === 0 ? "bg-white" : "bg-ivory"}`}
      data-testid={`methode-step-${step.number}`}
    >
      <div className="container-rozier grid grid-cols-12 gap-10 md:gap-16">
        <Reveal
          className={`col-span-12 md:col-span-4 ${
            reversed ? "md:order-2 md:col-start-9" : ""
          }`}
        >
          <p className="font-serif text-7xl md:text-8xl lg:text-[140px] leading-none gold-text">
            {step.number}
          </p>
          <p className="overline mt-6 text-graphite">{step.subtitle}</p>
          <h3 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05] font-light mt-3">
            {step.title}
          </h3>
        </Reveal>

        <Reveal
          className={`col-span-12 md:col-span-7 ${
            reversed ? "md:order-1 md:col-start-1" : "md:col-start-6"
          }`}
          delay={150}
        >
          <p className="font-serif italic text-graphite text-xl md:text-2xl leading-snug">
            {step.intro}
          </p>

          <div className="mt-10 space-y-8">
            {step.blocks.map((b, i) => (
              <div key={i} className="border-l-2 border-gold pl-6">
                <p className="overline text-charcoal mb-3">{b.label}</p>
                {b.text && (
                  <p className="text-graphite text-base md:text-[17px] leading-[1.85]">
                    {b.text}
                  </p>
                )}
                {b.list && (
                  <ul className="space-y-2 mt-2">
                    {b.list.map((li, j) => (
                      <li
                        key={j}
                        className="text-graphite text-base md:text-[17px] leading-[1.7] flex gap-3"
                      >
                        <span className="text-gold mt-1.5 inline-block w-2 h-px bg-gold flex-shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {step.closing && (
            <p className="mt-10 font-serif italic text-charcoal text-xl md:text-2xl leading-snug border-t border-stone pt-8">
              {step.closing}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const CTABand = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="methode-cta">
    <div className="container-rozier">
      <Reveal className="max-w-4xl mx-auto text-center">
        <p className="overline mb-6">Parlons de votre projet</p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.05] tracking-tight font-light">
          Votre projet mérite une
          <span className="italic gold-text"> lecture attentive</span> avant toute
          décision.
        </h2>
        <p className="mt-10 text-graphite text-base md:text-lg leading-[1.9] max-w-2xl mx-auto">
          Un terrain en prévision d'achat, un bâti existant, une maison, un
          appartement, une entreprise, une exploitation, un haras à optimiser ou un
          projet encore en réflexion. Posons ensemble les bases d'un lieu harmonieux,
          qui vous ressemble, qui vous soutient et qui dure.
        </p>
        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-charcoal text-white px-8 py-4 uppercase tracking-[0.28em] text-xs hover:bg-gold transition-colors duration-300"
            data-testid="methode-cta-contact"
          >
            Parlons de votre projet
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Methode() {
  return (
    <div data-testid="page-methode">
      <HeroHeader />
      <Devise />
      <Philosophie />
      <TroisTemps />
      {STEPS.map((step, i) => (
        <StepBlock key={step.number} step={step} index={i} />
      ))}
      <CTABand />
    </div>
  );
}
